import React, { useCallback, useId, useState } from "react";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import logo from "../../assets/logo.png";
import "./LandingPage.css";

interface FeatureRow {
  name: string;
  description: string;
}

interface PricingTierData {
  id: string;
  name: string;
  priceDollars: string;
  tagline: string;
  popular?: boolean;
  lines: string[];
  registerTo: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface HowStep {
  numeral: string;
  title: string;
  description: string;
}

const FEATURE_ROWS: FeatureRow[] = [
  {
    name: "Multi-tenant client management",
    description:
      "Add unlimited clients (limited by your plan tier). Track each client's disputes, score history, documents, and payment status.",
  },
  {
    name: "AI credit report parsing",
    description:
      "Upload a PDF credit report. The AI extracts the credit scores, negative tradelines, and inquiries automatically and creates dispute items.",
  },
  {
    name: "AI dispute letter generation",
    description:
      "Generate dispute letters with FCRA Section 611 and FDCPA citations. Letters are saved per client and editable before sending.",
  },
  {
    name: "Per-bureau dispute tracking",
    description:
      "Track Experian, Equifax, and TransUnion separately for every dispute item. Kanban board shows what's in progress, sent, and resolved.",
  },
  {
    name: "White-label client portal",
    description:
      "Clients log in to a portal that shows your brand, your colors, your domain. They never see the IntelliWarrior name.",
  },
  {
    name: "Stripe Connect client billing",
    description:
      "You connect your own Stripe account. Bill clients monthly. We take 3%. Money goes to your bank, not ours.",
  },
  {
    name: "Three-tier affiliate engine",
    description:
      "Refer other operators (20% recurring forever). Operators refer operators (15% for 12 months). Clients refer clients (you set the rate).",
  },
  {
    name: "CROA compliance enforcement",
    description:
      "The platform blocks client billing until the first dispute letter is sent. The AI never promises specific score outcomes.",
  },
  {
    name: "Email automation",
    description:
      "Welcome emails, dispute status updates, letter sent confirmations, dispute resolved notifications. Sent from your branded email.",
  },
  {
    name: "Forgot password & user management",
    description:
      "Standard auth with email reset, role-based access for staff (Admin, Advisor, Support).",
  },
  {
    name: "Score tracker",
    description: "Log credit scores over time. Charts show progress per bureau.",
  },
  {
    name: "Document upload & storage",
    description:
      "Clients upload IDs, utility bills, and supporting documents through their portal. You access them from the client file.",
  },
];

const HOW_STEPS: HowStep[] = [
  {
    numeral: "I.",
    title: "Sign up and brand it.",
    description:
      "Start your 14-day trial. Upload your logo, set your brand colors, and configure your custom domain (Agency plan).",
  },
  {
    numeral: "II.",
    title: "Add your first client.",
    description: "Add a client manually or import a list with the CSV importer. Upload their credit report.",
  },
  {
    numeral: "III.",
    title: "Generate disputes.",
    description:
      "The AI parses the report, identifies disputable items, and drafts dispute letters. You review, edit, and send.",
  },
  {
    numeral: "IV.",
    title: "Bill and track.",
    description:
      "Connect Stripe, set your client pricing, and bill clients through the platform. Track resolution rates and revenue.",
  },
];

const PRICING_TIERS: PricingTierData[] = [
  {
    id: "starter",
    name: "Starter",
    priceDollars: "$99",
    tagline: "For operators getting started.",
    lines: [
      "Up to 50 clients",
      "2 team seats",
      "Logo branding only",
      "Basic AI dispute features",
      "Email support",
    ],
    registerTo: "/register?plan=starter",
  },
  {
    id: "growth",
    name: "Growth",
    priceDollars: "$199",
    tagline: "For operators scaling past 50 clients.",
    popular: true,
    lines: [
      "Up to 200 clients",
      "6 team seats",
      "Full white-label (logo, colors, portal)",
      "Full AI features",
      "Priority support",
    ],
    registerTo: "/register?plan=growth",
  },
  {
    id: "agency",
    name: "Agency",
    priceDollars: "$399",
    tagline: "For operators running a full agency.",
    lines: [
      "Unlimited clients",
      "Unlimited team seats",
      "White-label + custom domain",
      "Full AI features",
      "Dedicated onboarding",
    ],
    registerTo: "/register?plan=agency",
  },
];

const FAQ_ITEMS: FaqItem[] = [
  {
    question: "Is this really white-label?",
    answer:
      "Yes. Your clients never see the IntelliWarrior name in the portal, in dispute letters, or in emails. On the Agency plan, you can point your own domain at the platform.",
  },
  {
    question: "What does the AI actually do?",
    answer:
      "It reads PDF credit reports, identifies disputable tradelines and inquiries, drafts dispute letters with FCRA and FDCPA citations, and tracks responses per bureau. Operators review and edit before anything is sent.",
  },
  {
    question: "Is the platform CROA compliant?",
    answer:
      "Yes. The platform blocks operator billing of any client until the first dispute letter is sent. This is enforced in the API, not just the UI.",
  },
  {
    question: "What happens after the 14-day trial?",
    answer:
      "Your card is charged for the plan you selected at signup. You can upgrade, downgrade, or cancel anytime. No contracts.",
  },
  {
    question: "Do I need my own Stripe account to bill clients?",
    answer:
      "Yes. You connect your Stripe account through Stripe Connect (about 5 minutes). Clients pay you directly. IntelliWarrior takes a 3% platform fee.",
  },
  {
    question: "Can I import existing clients?",
    answer: "Yes. CSV import with duplicate detection.",
  },
  {
    question: "What does the affiliate program pay?",
    answer:
      "External affiliates earn 20% recurring forever on operators they refer. Operators earn 15% for 12 months on other operators they refer. Clients can refer other clients at a commission rate the operator sets.",
  },
  {
    question: "Where is the data hosted?",
    answer:
      "The platform is hosted on US infrastructure with TLS encryption in transit and at rest. Each operator's data is isolated at the database query level.",
  },
];

const COMPLIANCE_CHECKLIST: string[] = [
  "CROA-compliant client billing (no upfront fees)",
  "AI guardrails — no guaranteed-outcome language",
  "Multi-tenant data isolation enforced at every query",
  "TLS encryption (Cloudflare + origin SSL)",
  "JWT auth with role-based access",
  "Audit log for terms acceptance and key actions",
];

const LandingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null);
  const mobilePanelId = useId();

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  const toggleFaq = useCallback((index: number) => {
    setFaqOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleFaqKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleFaq(index);
      }
    },
    [toggleFaq]
  );

  return (
    <div className="iw-landing">
      <header className="iw-header">
        <div className="iw-header-inner">
          <Link to="/" className="iw-header-brand" aria-label="IntelliWarrior home">
            <img src={logo} alt="" className="iw-header-logo iw-logo" width={32} height={32} />
            <span className="iw-header-wordmark">INTELLIWARRIOR</span>
          </Link>

          <nav className="iw-header-nav" aria-label="Primary">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#compliance">Compliance</a>
            <a href="#faq">FAQ</a>
          </nav>

          <div className="iw-header-actions">
            {isAuthenticated ? (
              <Link to="/dashboard" className="iw-link-dashboard">
                Go to Dashboard →
              </Link>
            ) : (
              <>
                <Link to="/login" className="iw-link-signin">
                  Sign In
                </Link>
                <Link to="/register" className="iw-btn-header">
                  <span className="iw-btn-header-label-full">Begin trial</span>
                  <span className="iw-btn-header-label-short">Trial</span>
                </Link>
              </>
            )}
          </div>

          <div className="iw-header-right-mobile">
            {!isAuthenticated ? (
              <Link to="/register" className="iw-btn-header" onClick={closeMobileMenu} aria-label="Begin trial">
                <span className="iw-btn-header-label-full">Begin trial</span>
                <span className="iw-btn-header-label-short">Trial</span>
              </Link>
            ) : (
              <Link to="/dashboard" className="iw-link-dashboard" onClick={closeMobileMenu}>
                Go to Dashboard →
              </Link>
            )}
            <button
              type="button"
              className="iw-header-mobile-toggle"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls={mobilePanelId}
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              <Menu size={24} strokeWidth={2} aria-hidden />
            </button>
          </div>

          <div
            id={mobilePanelId}
            className={`iw-header-mobile-panel ${mobileMenuOpen ? "is-open" : ""}`}
            role="navigation"
            aria-label="Mobile primary"
            aria-hidden={!mobileMenuOpen}
          >
            <a href="#features" onClick={closeMobileMenu}>
              Features
            </a>
            <a href="#pricing" onClick={closeMobileMenu}>
              Pricing
            </a>
            <a href="#compliance" onClick={closeMobileMenu}>
              Compliance
            </a>
            <a href="#faq" onClick={closeMobileMenu}>
              FAQ
            </a>
            <div className="iw-header-mobile-actions">
              {isAuthenticated ? (
                <Link to="/dashboard" className="iw-btn-header" onClick={closeMobileMenu}>
                  Go to Dashboard →
                </Link>
              ) : (
                <>
                  <Link to="/login" className="iw-link-signin" onClick={closeMobileMenu}>
                    Sign In
                  </Link>
                  <Link to="/register" className="iw-btn-header" onClick={closeMobileMenu}>
                    Begin trial
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="iw-page-body">
        <section className="iw-hero" aria-labelledby="iw-hero-heading">
          <div>
            <p className="iw-hero-eyebrow">Credit Repair · Operator Infrastructure · Est. 2026</p>
            <h1 id="iw-hero-heading">Credit repair software for credit repair business professionals.</h1>
            <p className="iw-hero-lede">
              IntelliWarrior is a complete credit repair platform you run under your own brand. Manage clients, generate AI dispute letters, bill clients through Stripe, and pay affiliate referrals — all from one dashboard your clients never see our name on.
            </p>
            <div className="iw-hero-cta">
              <Link to="/register" className="iw-btn-primary">
                Begin 14-day trial →
              </Link>
              <a className="iw-link-secondary" href="#pricing">
                See pricing
              </a>
            </div>
          </div>

          <aside className="iw-ai-callout" aria-label="IntelliWarrior AI">
            <div className="iw-ai-eyebrow">— INTELLIWARRIOR AI —</div>
            <h3 className="iw-ai-headline">Built to handle the heavy lifting.</h3>
            <p className="iw-ai-body">
              Upload a credit report. IntelliWarrior AI reads every page, extracts the credit scores, the negative tradelines, and the inquiries. It identifies what&apos;s disputable under FCRA. It drafts dispute letters citing the right statute for each item. It tracks responses across all three bureaus.
            </p>
            <p className="iw-ai-body">What used to take an hour per client takes minutes.</p>
            <p className="iw-ai-closer">You stay in control. The AI does the typing.</p>
          </aside>
        </section>

        <div className="iw-pillars">
          <article className="iw-pillar">
            <p className="iw-pillar-roman">— I —</p>
            <h3>White-label</h3>
            <p>Your domain, your logo, your colors, your client portal. Your clients never see the IntelliWarrior name anywhere.</p>
          </article>
          <article className="iw-pillar">
            <p className="iw-pillar-roman">— II —</p>
            <h3>AI dispute letters</h3>
            <p>
              Upload a credit report. The AI extracts negative tradelines and generates dispute letters citing FCRA Section 611 and FDCPA. Letters are saved to the client file and ready to print or email.
            </p>
          </article>
          <article className="iw-pillar">
            <p className="iw-pillar-roman">— III —</p>
            <h3>Stripe Connect billing</h3>
            <p>
              Connect your Stripe account, set your client pricing, and bill clients on your account. IntelliWarrior takes a 3% platform fee. Cannot bill before the first dispute letter is sent (CROA compliance).
            </p>
          </article>
          <article className="iw-pillar">
            <p className="iw-pillar-roman">— IV —</p>
            <h3>Three-tier affiliate program</h3>
            <p>
              Earn 20% recurring forever on operators you refer. Operators earn 15% for 12 months on operators they refer. Clients can refer other clients with operator-set commissions.
            </p>
          </article>
        </div>

        <section id="features" className="iw-section">
          <div className="iw-section-head">
            <div className="eyebrow">Features</div>
            <h2>What you get</h2>
          </div>
          <div className="iw-feature-grid">
            {FEATURE_ROWS.map((row) => (
              <React.Fragment key={row.name}>
                <div className="iw-feature-name">{row.name}</div>
                <div className="iw-feature-desc">{row.description}</div>
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="iw-section" aria-labelledby="iw-how-heading">
          <div className="iw-section-head iw-section-head--solo">
            <h2 id="iw-how-heading">How it works</h2>
          </div>
          <div className="iw-steps">
            {HOW_STEPS.map((step) => (
              <div key={step.numeral} className="iw-step-row">
                <div className="iw-step-num" aria-hidden>
                  {step.numeral}
                </div>
                <div className="iw-step-body">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="pricing" className="iw-section">
          <div className="iw-section-head">
            <div className="eyebrow">Pricing</div>
            <h2>Pricing</h2>
          </div>
          <div className="iw-pricing-row">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.id}
                className={`iw-pricing-tier${tier.popular ? " iw-pricing-tier--featured" : ""}`}
              >
                {tier.popular ? <p className="iw-pricing-flag">Most Popular</p> : null}
                <h3>{tier.name}</h3>
                <p className="iw-pricing-price">
                  {tier.priceDollars} <span>/mo</span>
                </p>
                <p className="iw-pricing-tagline">
                  <em>{tier.tagline}</em>
                </p>
                <hr className="iw-pricing-divider" />
                <ul className="iw-pricing-list">
                  {tier.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <Link to={tier.registerTo} className="iw-btn-primary iw-btn-pricing">
                  Begin trial →
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section id="compliance" className="iw-section">
          <div className="iw-section-head">
            <div className="eyebrow">Compliance</div>
            <h2>Compliance</h2>
          </div>
          <div className="iw-compliance-grid">
            <div className="iw-compliance-prose">
              <p>Credit repair is regulated under federal law. The platform enforces compliance at the code level so operators don&apos;t have to think about it.</p>
              <p>
                CROA prohibits charging clients before services are delivered. IntelliWarrior blocks billing for any client whose first dispute letter has not been sent. This is not a setting you can turn off.
              </p>
              <p>
                The AI is constrained to never promise specific score outcomes, never claim guaranteed results, and never produce language that violates FCRA or FDCPA. Every dispute letter cites the relevant statute.
              </p>
            </div>
            <ul className="iw-compliance-list">
              {COMPLIANCE_CHECKLIST.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section id="faq" className="iw-section">
          <div className="iw-section-head">
            <div className="eyebrow">Questions</div>
            <h2>Questions</h2>
          </div>
          <ul className="iw-faq-list">
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = faqOpenIndex === index;
              const panelId = `iw-faq-panel-${index}`;
              const triggerId = `iw-faq-trigger-${index}`;
              return (
                <li key={item.question} className="iw-faq-item">
                  <button
                    type="button"
                    id={triggerId}
                    className="iw-faq-trigger"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleFaq(index)}
                    onKeyDown={(e) => handleFaqKeyDown(e, index)}
                  >
                    <span>{item.question}</span>
                    <span className="iw-faq-icon" aria-hidden>
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  <p
                    id={panelId}
                    className="iw-faq-answer"
                    role="region"
                    aria-labelledby={triggerId}
                    hidden={!isOpen}
                  >
                    {item.answer}
                  </p>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <section className="iw-final-cta" aria-labelledby="iw-final-cta-heading">
        <div className="iw-final-cta-inner">
          <h2 id="iw-final-cta-heading">Run your credit repair business on IntelliWarrior.</h2>
          <p className="iw-final-cta-lede">14-day trial. Cancel anytime. Your brand only.</p>
          <Link to="/register" className="iw-btn-primary-inverted">
            Begin 14-day trial →
          </Link>
        </div>
      </section>

      <footer className="iw-footer">
        <div className="iw-footer-inner">
          <div className="iw-footer-grid">
            <div className="iw-footer-col iw-footer-brand">
              <h4>IntelliWarrior</h4>
              <img src={logo} alt="" className="iw-footer-logo iw-logo" width={32} height={32} />
              <p className="iw-footer-tagline">White-label credit repair software for credit repair businesses.</p>
            </div>
            <div className="iw-footer-col">
              <h4>Product</h4>
              <ul>
                <li>
                  <a href="#features">Features</a>
                </li>
                <li>
                  <a href="#pricing">Pricing</a>
                </li>
                <li>
                  <a href="#compliance">Compliance</a>
                </li>
                <li>
                  <a href="#faq">FAQ</a>
                </li>
              </ul>
            </div>
            <div className="iw-footer-col">
              <h4>Legal</h4>
              <ul>
                <li>
                  <Link to="/terms">Terms of Service</Link>
                </li>
                <li>
                  <Link to="/privacy">Privacy Policy</Link>
                </li>
              </ul>
            </div>
            <div className="iw-footer-col">
              <h4>Contact</h4>
              <p>
                <a href="mailto:info@intelliwarrior.com">info@intelliwarrior.com</a>
              </p>
              <p>Warrior Financial Services LLC · Chicago, IL</p>
            </div>
          </div>
          <p className="iw-footer-meta">
            © 2026 Warrior Financial Services LLC. All rights reserved. IntelliWarrior is a registered trademark.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
